/**
 * TagInput — multi-select tag combobox.
 *
 * Accepts tags as a controlled string[]; emits onChange with the new array.
 * Autocompletes from the server's /api/attachments/tags endpoint.
 *
 * Keyboard:
 *   - Enter or comma → accept current input as chip
 *   - Backspace in empty input → remove last chip
 *
 * All values lowercased + trimmed; leading '#' stripped. Whitespace inside a
 * tag rejected.
 */
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useTagAutocomplete } from '@/lib/attachments-queries';

interface Props {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  className?: string;
}

function normalise(input: string): string | null {
  let s = input.trim().toLowerCase();
  if (s.startsWith('#')) s = s.slice(1).trim();
  if (s.length === 0) return null;
  if (s.length > 50) return null;
  if (/\s/.test(s)) return null;
  return s;
}

export function TagInput({ value, onChange, placeholder = 'Type to add a tag…', className }: Props) {
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const auto = useTagAutocomplete(text);

  const addTag = (raw: string) => {
    const t = normalise(raw);
    if (!t) return;
    if (value.includes(t)) {
      setText('');
      return;
    }
    onChange([...value, t]);
    setText('');
  };

  const removeAt = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      if (text.length > 0) {
        e.preventDefault();
        addTag(text);
      }
    } else if (e.key === 'Backspace' && text.length === 0 && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    } else if (e.key === 'Escape') {
      setFocused(false);
      inputRef.current?.blur();
    }
  };

  // Close suggestions when focus leaves the whole container
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const suggestions = (auto.data?.tags ?? []).filter((t) => !value.includes(t.name)).slice(0, 8);
  const showSuggestions = focused && suggestions.length > 0;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex flex-wrap items-center gap-1 rounded-sm border border-border-default bg-surface px-2 py-1.5 text-sm sm:py-1',
        focused && 'border-accent',
        className,
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((tag, idx) => (
        <span
          key={`${tag}-${idx}`}
          className="inline-flex items-center gap-1 rounded-sm bg-accent/10 px-2 py-1 text-sm text-accent-strong sm:gap-0.5 sm:px-1.5 sm:py-0.5 sm:text-xs"
        >
          #{tag}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              removeAt(idx);
            }}
            aria-label={`Remove ${tag}`}
            // Mobile: bigger tap target inside the chip
            className="-mr-1 inline-flex h-6 w-6 items-center justify-center rounded-sm hover:bg-accent/20 sm:h-auto sm:w-auto sm:p-0.5"
          >
            <X className="h-3 w-3 sm:h-2.5 sm:w-2.5" strokeWidth={2} />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => setFocused(true)}
        placeholder={value.length === 0 ? placeholder : ''}
        className="min-h-[28px] min-w-[120px] flex-1 bg-transparent text-sm text-primary outline-none"
      />
      {showSuggestions ? (
        <ul className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-sm border border-border-default bg-surface shadow-[var(--shadow-overlay)]">
          {suggestions.map((s) => (
            <li key={s.name}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  addTag(s.name);
                }}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-secondary hover:bg-subtle hover:text-primary sm:px-2 sm:py-1 sm:text-xs"
              >
                <span className="font-mono">#{s.name}</span>
                <span className="tabular-nums text-tertiary">{s.usageCount}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
